import VehicleOffering from '@carvana/merch-contracts/VehicleOffering';
import { useMemo } from 'react';
import {
  booleanFacets,
  facetsWithDisplayName,
  staticDisplayNameFacets,
  rangeFacets
} from '~/constants/common/facets';
import type {
  AppliedFilters,
  AvailabilityType,
  FacetData,
  FacetListKeys,
  FacetRanges,
  FacetTag,
  FinanceType,
  ListItem,
  MakeType,
  ModelType,
  ModelsKey,
  RangeFacet
} from '~/contracts/common';
import { isDefined } from '~/utilities/array';
import addDaysToDate from '~/utilities/date/addDaysToDate';
import getFullDate from '~/utilities/date/getFullDate';
import getWeekDay from '~/utilities/date/getWeekDay';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { logError } from '~/utilities/loggers';

const staticDisplayNameMap: Record<string, Record<string, string>> = {
  seatMaterials: {
    'cloth seats': 'Cloth Seats',
    'genuine leather seats': 'Genuine Leather Seats',
    'synthetic leather seats': 'Synthetic Leather Seats'
  },
  luxuryTypes: {
    'luxury-entry': 'Luxury',
    'luxury-mid': 'Luxury',
    'luxury-high': 'Ultra Luxury',
    'exotic luxury': 'Ultra Luxury',
    'non-luxury': 'Everyday Comfort'
  },
  sportTypes: {
    sport: 'Sport-Tuned',
    'fwd sport': 'Sport-Tuned',
    'high performance': 'High Performance',
    'exotic sport': 'High Performance',
    'non-sport': 'Everyday Driving'
  }
};

// Pick the drawer that has entries: an empty `models: []` is truthy, so a plain
// truthy check would select it over a populated `parentModels`.
const getAppliedModelsKey = (make: MakeType): ModelsKey =>
  make.models?.length ? 'models' : 'parentModels';

// we use the duplicate trims to decide if we need to show "<Model> <Trim>" or just "<Trim>" for trim tags
const getDuplicateTrims = (makes: MakeType[]): string[] => {
  const allTrims =
    makes.flatMap(
      make => make[getAppliedModelsKey(make)]?.flatMap(model => model?.trims)
    ) ?? [];

  return allTrims
    .filter(
      (trim, index, array) =>
        array.indexOf(trim) !== index && array.lastIndexOf(trim) === index
    )
    .filter(isDefined);
};

const getAvailabilityTag = (
  facetKey: 'earliestDayToDeliveryOrPickup',
  values: AvailabilityType
): FacetTag => {
  const day = addDaysToDate(values.days);
  const date = getFullDate(day);

  const tag: FacetTag = {
    context: { key: facetKey },
    facetKey,
    tagName: ''
  };

  const getItTomorrow = values.days === 1;
  if (getItTomorrow) tag.tagName = 'Get It By Tomorrow';

  const getItByDay = values.days > 1 && values.days <= 7;
  if (getItByDay) tag.tagName = `Get it by ${getWeekDay(day)}`;

  const getItByDate = values.days > 7;
  if (getItByDate) tag.tagName = `Get it by ${date}`;

  return tag;
};

function getMakeModelTrimTags(
  facetKey: 'makes',
  values: MakeType[]
): FacetTag[] {
  const duplicateTrims = getDuplicateTrims(values);

  return values.flatMap(make => {
    const makeData: FacetTag = {
      context: {
        key: make.name
      },
      facetKey,
      tagName: make.name
    };

    const modelsKey: ModelsKey = getAppliedModelsKey(make);
    const models = make[modelsKey] ?? [];

    const modelData = models.map<FacetTag>(model => ({
      context: {
        key: model.name,
        modelsKey,
        parents: [make.name]
      },
      facetKey,
      tagName: model.name
    }));

    const trimData = models.flatMap<FacetTag>((model: ModelType) =>
      (model.trims ?? []).map<FacetTag>(trim => ({
        context: {
          key: trim,
          modelsKey,
          parents: [make.name, model.name]
        },
        facetKey,
        tagName: duplicateTrims.includes(trim) ? `${model.name} ${trim}` : trim
      }))
    );

    return [makeData, ...modelData, ...trimData];
  });
}

type Range =
  | {
      type: 'equal';
      value: number;
    }
  | {
      type: 'extreme';
      side: 'min' | 'max';
      value: number;
    }
  | {
      type: 'range';
      value: Required<RangeFacet>;
    };

function getRange(value: RangeFacet): Range {
  if (value.min == null || value.max == null) {
    return {
      type: 'extreme',
      side: value.min ? 'min' : 'max',
      value: value.min ?? value.max ?? 0
    };
  }

  if (value.min === value.max) {
    return { type: 'equal', value: value.min };
  }

  return {
    type: 'range',
    value: { max: value.max, min: value.min }
  };
}

function getRangeFilterTag(
  facetKey: keyof FacetRanges | 'finance',
  value: RangeFacet & FinanceType
): FacetTag {
  const range = getRange(value);

  switch (facetKey) {
    case 'finance': {
      const tagName = (function () {
        const { downPayment, monthlyPayment } = value;

        if (downPayment != null && monthlyPayment) {
          return `$${formatAndDropDecimal(
            downPayment
          )} down & $${formatAndDropDecimal(monthlyPayment)}/mo.`;
        } else if (downPayment != null) {
          return `$${formatAndDropDecimal(downPayment)} Down`;
        } else if (monthlyPayment) {
          return `$${formatAndDropDecimal(monthlyPayment)}/mo.`;
        } else {
          return '';
        }
      })();

      return {
        context: { key: facetKey },
        facetKey,
        tagName
      };
    }

    case 'mileage':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value.toLocaleString()} miles`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value.toLocaleString()} miles or more`
                : `Under ${range.value.toLocaleString()} miles`;
            case 'range':
              return `${range.value.min.toLocaleString()} - ${range.value.max.toLocaleString()} miles`;
          }
        })()
      };

    case 'mpg':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value.toLocaleString()} MPG`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value.toLocaleString()} MPG or better`
                : `Up to ${range.value.toLocaleString()} MPG`;
            case 'range':
              return `${range.value.min.toLocaleString()} - ${range.value.max.toLocaleString()} MPG`;
          }
        })()
      };

    case 'price': {
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `$${formatAndDropDecimal(range.value)}`;
            case 'extreme':
              return range.side === 'min'
                ? `$${formatAndDropDecimal(range.value)} and up`
                : `Under $${formatAndDropDecimal(range.value)}`;
            case 'range':
              return `$${formatAndDropDecimal(
                range.value.min
              )} - $${formatAndDropDecimal(range.value.max)}`;
          }
        })()
      };
    }

    case 'seatingCapacity':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value} seats`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value} or more seats`
                : `Up to ${range.value} seats`;
            case 'range':
              return `${range.value.min} - ${range.value.max} seats`;
          }
        })()
      };

    case 'towingCapacity':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value.toLocaleString()} Towing Capacity`;
            case 'extreme':
              return range.side === 'min'
                ? `Over ${range.value.toLocaleString()} Towing Capacity`
                : `Under ${range.value.toLocaleString()} Towing Capacity`;
            case 'range':
              return `${range.value.min.toLocaleString()} - ${range.value.max.toLocaleString()} Towing Capacity`;
          }
        })()
      };

    case 'year':
      return {
        context: { key: facetKey },
        facetKey,
        tagName: (function () {
          switch (range.type) {
            case 'equal':
              return `${range.value}`;
            case 'extreme':
              return range.side === 'min'
                ? `${range.value} or newer`
                : `${range.value} or older`;
            case 'range':
              return `${range.value.min} - ${range.value.max}`;
          }
        })()
      };

    default:
      return {
        context: { key: facetKey },
        facetKey,
        tagName: ''
      };
  }
}

function resolveStaticDisplayNameFacetTags(
  facetKey: FacetListKeys | 'isFreeDelivery',
  values: string[]
): FacetTag[] {
  const displayNameMap = staticDisplayNameMap[facetKey];
  const tagsByDisplayName = values.reduce<Record<string, string[]>>(
    (acc, item) => {
      const tagName = displayNameMap[item.toLowerCase()] ?? item;
      acc[tagName] = [...(acc[tagName] ?? []), item];

      return acc;
    },
    {}
  );

  return Object.entries(tagsByDisplayName).map(([tagName, keys]) => ({
    context: { key: keys[0], keys },
    facetKey,
    tagName
  }));
}

const resolveListFacetTags = (
  facetData: Partial<FacetData>,
  facetKey: FacetListKeys | 'isFreeDelivery',
  value: any
): FacetTag[] => {
  if (facetKey === 'cvnaFeatureIds') {
    const items = Object.values(facetData.cvnaFeatureDetails ?? {}).flat();

    return value
      .map((id: string) => items.find(item => item.key === id))
      .filter(isDefined)
      .map((item: ListItem) => ({
        context: { key: item.key },
        facetKey,
        tagName: item.displayName
      }));
  }

  if (facetKey === 'cvnaFeatures') {
    return value.map((feature: string) => ({
      context: { key: feature },
      facetKey,
      tagName: feature
    }));
  }

  if (facetKey === 'isFreeDelivery') {
    return value
      ? [
          {
            context: { key: facetKey },
            facetKey,
            tagName: facetData?.waysToSave?.isFreeDelivery?.displayName ?? ''
          }
        ]
      : [];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  const isColorFacet = facetKey === 'colors' || facetKey === 'interiorColors';

  return (
    facetData[facetKey]?.buckets
      ?.filter(v => value.includes(v.key))
      ?.map<FacetTag>(v => ({
        context: { key: v.key },
        tagName: isColorFacet
          ? v.displayName.replace(/^Other\b/, 'Unspecified')
          : v.displayName,
        facetKey
      })) ?? []
  );
};

type Options = {
  displayNewCars?: boolean;
  includeVehicleAlerts?: boolean;
  shouldUseTrimLevelAndTrimFilters?: boolean;
  shouldGenerate?: boolean;
};

/**
 * We use this to get the filter tags for each applied filter.
 * To do this we loop through facet data and for each applied filter
 * we either return the displayName or build a tag for it.
 * The only tags we need to build still are Range tags.
 */
export default function useGetFilterTags(
  baseline: Partial<FacetData>,
  filters: AppliedFilters,
  opts: Options = {}
): FacetTag[] {
  const {
    displayNewCars = true,
    includeVehicleAlerts = false,
    shouldUseTrimLevelAndTrimFilters = false,
    shouldGenerate = true
  } = opts;

  return useMemo<FacetTag[]>(() => {
    if (!shouldGenerate) return [];

    const getTags = (
      tags: FacetTag[],
      [facetKey, value]: [any, any]
    ): FacetTag[] => {
      try {
        const isEmpty =
          Object.keys(value).length === 0 && !booleanFacets.includes(facetKey);
        if (isEmpty) return tags;

        if (
          displayNewCars &&
          facetKey === 'offerings' &&
          value.includes(VehicleOffering.New)
        ) {
          return [
            ...tags,
            {
              context: { key: VehicleOffering.New },
              facetKey,
              tagName: 'Brand New Cars '
            }
          ];
        }

        if (
          (facetKey === 'trims' || facetKey === 'trimLevel') &&
          shouldUseTrimLevelAndTrimFilters
        ) {
          return [
            ...tags,
            ...value.map((trim: string) => ({
              context: { key: trim },
              facetKey,
              tagName: trim
            }))
          ];
        }

        if (staticDisplayNameFacets.includes(facetKey)) {
          return [
            ...tags,
            ...resolveStaticDisplayNameFacetTags(facetKey, value)
          ];
        }

        if (facetsWithDisplayName.includes(facetKey)) {
          const tagsFromFacetData = resolveListFacetTags(
            baseline,
            facetKey,
            value
          );

          return [...tags, ...(tagsFromFacetData ?? [])];
        }

        if (facetKey === 'earliestDayToDeliveryOrPickup') {
          const availabilityTag = getAvailabilityTag(facetKey, value);

          return [...tags, availabilityTag];
        }

        if (rangeFacets.includes(facetKey)) {
          const rangeTag = getRangeFilterTag(facetKey, value);

          return [...tags, rangeTag];
        }

        if (facetKey === 'makes') {
          const makesTags = getMakeModelTrimTags(facetKey, value) ?? [];

          return [...tags, ...makesTags];
        }

        if (facetKey === 'vehicleIds' && includeVehicleAlerts) {
          return [
            ...tags,
            {
              context: { key: facetKey },
              facetKey,
              tagName: 'Vehicle alerts'
            }
          ];
        }

        return tags;
      } catch (e) {
        logError(new Error('Failed to generate filter tags'), {
          location: 'useGetFilterTags',
          facetKey,
          value
        });

        return [];
      }
    };

    const filterTags = Object.entries(filters ?? {}).reduce(getTags, []);

    return filterTags ?? [];
  }, [
    baseline,
    displayNewCars,
    filters,
    includeVehicleAlerts,
    shouldUseTrimLevelAndTrimFilters,
    shouldGenerate
  ]);
}
